import { useState, useCallback, useEffect, useRef } from "react";
import { useRouter } from "nextjs-toploader/app";
import { useQueryClient } from "@tanstack/react-query";
import {
  getMessageNotifications,
  markAllAsRead,
  markAsRead,
} from "@/services/notification.service";
import {
  MessageNotificationItem,
  NotificationItem,
} from "@/interface/notification.types";
import { showDynamicToast, TOAST_TYPES } from "@/lib/toast-utils/toast.util";
import { useMessageNotificationCount } from "../notification/useMessageNotificationCount.hooks";
import useHeader from "../useHeader";

interface UseMessageNotificationListReturn {
  messageNotifications: MessageNotificationItem[];
  loading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  fetchMoreMessageNotifications: () => void;
  refreshMessageNotifications: () => void;
  totalCount: number;
  handleMarkAllAsRead: () => Promise<void>;
  handleMarkAsRead: (notificationId: string) => Promise<void>;
  handleMessageNotificationClick: (notification: MessageNotificationItem, onNavigate?: () => void) => void;
  expandedIds: string[];
  toggleExpand: (id: string) => void;
}

interface UseMessageNotificationListProps {
  userType?: "Contractor" | "Homeowner";
  isOpen?: boolean;
}

export const useMessageNotificationList = (props?: UseMessageNotificationListProps): UseMessageNotificationListReturn => {
  const userType = props?.userType || "Contractor";
  const router = useRouter();
  const queryClient = useQueryClient();
  const [messageNotifications, setMessageNotifications] = useState<MessageNotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const loadingRef = useRef(false);
  const fetchingPageRef = useRef<number | null>(null);

  const { isLoggedIn } = useHeader();
  const { data: unreadCount } = useMessageNotificationCount(isLoggedIn);

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }, []);

  const fetchMessageNotifications = useCallback(
    async (pageNum: number = 1, isRefresh: boolean = false) => {
      if (!isLoggedIn) return;
      if (
        loadingRef.current ||
        (fetchingPageRef.current === pageNum && !isRefresh)
      )
        return;

      fetchingPageRef.current = pageNum;
      loadingRef.current = true;
      if (pageNum === 1 && !isRefresh) {
        setLoading(true);
      } else if (pageNum > 1) {
        setIsLoadingMore(true);
      }
      setError(null);

      try {
        const response = await getMessageNotifications({
          pageNumber: pageNum,
          pageSize: 10,
          query: "",
          filters: "",
          sorts: "",
        });

        if (isRefresh || pageNum === 1) {
          setMessageNotifications(response.data.data);
        } else {
          setMessageNotifications((prev) => {
            const newMessageNotifications = response.data.data.filter(
              (newItem) => !prev.some((prevItem) => prevItem.id === newItem.id)
            );
            return [...prev, ...newMessageNotifications];
          });
        }

        setTotalCount(response.data.count);

        const totalPages = response.meta.pagination.totalPages;
        setHasMore(pageNum < totalPages);

        if (!isRefresh) {
          setPage(pageNum);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch notifications"
        );
      } finally {
        setLoading(false);
        setIsLoadingMore(false);
        loadingRef.current = false;
        if (isRefresh) {
          fetchingPageRef.current = null;
        }
      }
    },
    [isLoggedIn]
  );

  const fetchMoreMessageNotifications = useCallback(() => {
    if (hasMore && !loading && !isLoadingMore) {
      fetchMessageNotifications(page + 1);
    }
  }, [hasMore, loading, isLoadingMore, page, fetchMessageNotifications]);

  const refreshMessageNotifications = useCallback(() => {
    setPage(1);
    fetchMessageNotifications(1, true);
  }, [fetchMessageNotifications]);

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      // Optimistically clear all unread counts
      setMessageNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setTotalCount(0);
      
      // Optimistically update the badge count in React Query cache
      queryClient.setQueryData(["unreadMessageNotificationCount"], (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          data: {
            ...oldData.data,
            count: 0,
          },
        };
      });

      await markAllAsRead();
      
      // Invalidate notification count query to update badges in the UI from server
      await queryClient.invalidateQueries({ queryKey: ["unreadMessageNotificationCount"] });
      // Refresh the local list to get final state
      refreshMessageNotifications();
      showDynamicToast(TOAST_TYPES.success, "Success", "All notifications have been marked as read");
    } catch (error) {
      console.error("Failed to mark all as read:", error);
      showDynamicToast(TOAST_TYPES.error, "Error", "Failed to mark all notifications as read");
      refreshMessageNotifications();
    }
  }, [refreshMessageNotifications, queryClient]);

  const handleMarkAsRead = useCallback(async (notificationId: string) => {
    try {
      // Find the notification to check if it was unread
      const notification = messageNotifications.find(n => n.id === notificationId);
      const wasUnread = notification && !notification.isRead;

      // Optimistically update the notification as read in the local state
      setMessageNotifications(prev => 
        prev.map(n => 
          n.id === notificationId 
            ? { ...n, isRead: true }
            : n
        )
      );
      
      if (wasUnread) {
        // Optimistically update local count
        setTotalCount(prev => Math.max(0, prev - 1));
        
        // Optimistically update the badge count in React Query cache
        queryClient.setQueryData(["unreadMessageNotificationCount"], (oldData: any) => {
          if (!oldData || !oldData.data) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              count: Math.max(0, (oldData.data.count || 0) - 1),
            },
          };
        });
      }
      
      await markAsRead(notificationId);
      
      // Invalidate notification count query to update badges in the UI from server
      await queryClient.invalidateQueries({ queryKey: ["unreadMessageNotificationCount"] });
      
      // Refresh the list to get the updated state from the server
      refreshMessageNotifications();
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
      // Revert optimistic update on error by refreshing the list
      refreshMessageNotifications();
      queryClient.invalidateQueries({ queryKey: ["unreadMessageNotificationCount"] });
    }
  }, [messageNotifications, refreshMessageNotifications, queryClient]);

  const handleMessageNotificationClick = useCallback(async (notification: NotificationItem, onNavigate?: () => void) => {
    // Trigger logo animation
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("nextjs-toploader:start"));
    }

    // Extract IDs favoring top-level fields (new) then data object (legacy)
    const proposalId = (notification as any).proposalId || notification.data?.ProposalId;
    const projectId = (notification as any).projectId || notification.data?.ProjectId;

    if (proposalId) {
      if (onNavigate) {
        onNavigate();
      }
      
      if (userType === "Homeowner") {
        router.push(`/homeowner/messages?proposal_id=${proposalId}`);
      } else {
        router.push(`/contractor/messages?proposal_id=${proposalId}`);
      }
      return;
    }

    // Fallback logic for notifications that don't have a proposalId (e.g. general project updates)
    if (projectId) {
      if (onNavigate) {
        onNavigate();
      }
      
      if (userType === "Homeowner") {
        router.push(`/homeowner/projects/${projectId}`);
      } else {
        if (notification.notificationType === "ReviewReceived") {
          router.push(`/contractor/reviews?projectId=${projectId}`);
        } else {
          router.push(`/contractor/projects/${projectId}`);
        }
      }
    }
  }, [router, userType, handleMarkAsRead]);

  // Initial fetch
  useEffect(() => {
    if (!isLoggedIn) return;
    fetchMessageNotifications(1);
  }, [isLoggedIn, fetchMessageNotifications]);

  // Fetch notifications when dropdown opens or new unread messages arrive while open
  useEffect(() => {
    if (props?.isOpen) {
      refreshMessageNotifications();
    }
  }, [props?.isOpen, refreshMessageNotifications, unreadCount]);

  return {
    messageNotifications,
    loading,
    isLoadingMore,
    error,
    hasMore,
    fetchMoreMessageNotifications,
    refreshMessageNotifications,
    totalCount,
    handleMarkAllAsRead,
    handleMarkAsRead,
    handleMessageNotificationClick,
    expandedIds,
    toggleExpand,
  };
};
